import { Workbook } from 'exceljs';
import { DateTime as DT } from 'luxon';
import type { StockRow } from '@econtractor/types';
import { getBaseCurrencySymbol, getCurrencySymbol } from 'utils/helpers/currency/currencyHelpers';

export type ExportStocksLabels = {
    prodName: string;
    category: string;
    article: string;
    balance: string;
    countEnd: string;
    updated: string;
    price: string;
    rate: string;
};

/** Колонки й партійність — як у таблиці залишків на момент експорту. */
export type ExportStocksView = {
    showCategory: boolean;
    showArticle: boolean;
    showBalance: boolean;
    showUpdated: boolean;
    showLots: boolean;
    /** Дві валюти: базова ціна, локальна ціна і курс. */
    showBothCurrencies: boolean;
};

export type ExportStockLot = {
    operPrice: number | null;
    operPriceRate: number | null;
    stock: number;
};

type SheetColumn = {
    header: string;
    key: string;
    width: number;
    style?: { numFmt: string };
};

const MONEY_FMT = '#,##0.00';

const downloadBlob = (blob: Blob, fileName: string) => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
};

const lotMoney = (value: number | null): number => Number(value || 0);

const lotRate = (lot: ExportStockLot): number =>
    Number(lot.operPriceRate || 0) / (Number(lot.operPrice) || 1);

const productCells = (stock: StockRow, view: ExportStocksView): Record<string, string | number | null> => {
    const row: Record<string, string | number | null> = {
        prodName: stock.prodName || '',
    };
    if (view.showCategory) row.category = stock.categoryName || '-';
    if (view.showArticle) row.article = stock.prodArticle || '';
    if (view.showBalance) {
        row.balance = stock.stockBalance ? -Number(stock.stockBalance) : null;
    }
    if (view.showUpdated) {
        row.updated = stock.stockUpdated
            ? DT.fromMillis(stock.stockUpdated).toFormat('dd.MM.yyyy')
            : '';
    }
    return row;
};

const buildColumns = (labels: ExportStocksLabels, view: ExportStocksView): SheetColumn[] => {
    const columns: SheetColumn[] = [
        { header: labels.prodName, key: 'prodName', width: 40 },
    ];
    if (view.showCategory) columns.push({ header: labels.category, key: 'category', width: 22 });
    if (view.showArticle) columns.push({ header: labels.article, key: 'article', width: 18 });
    if (view.showBalance) {
        columns.push({ header: labels.balance, key: 'balance', width: 16, style: { numFmt: MONEY_FMT } });
    }
    if (view.showUpdated) columns.push({ header: labels.updated, key: 'updated', width: 14 });
    if (view.showLots) {
        if (view.showBothCurrencies) {
            columns.push({
                header: `${labels.price}, ${getBaseCurrencySymbol()}`,
                key: 'price',
                width: 18,
                style: { numFmt: MONEY_FMT },
            });
            columns.push({
                header: `${labels.price}, ${getCurrencySymbol()}`,
                key: 'priceLocal',
                width: 18,
                style: { numFmt: MONEY_FMT },
            });
            columns.push({ header: labels.rate, key: 'rate', width: 12, style: { numFmt: MONEY_FMT } });
        } else {
            columns.push({
                header: `${labels.price}, ${getCurrencySymbol()}`,
                key: 'price',
                width: 18,
                style: { numFmt: MONEY_FMT },
            });
        }
    }
    columns.push({ header: labels.countEnd, key: 'countEnd', width: 12 });
    return columns;
};

export const exportStocksToExcel = async (
    stocks: StockRow[],
    warehouseId: number,
    labels: ExportStocksLabels,
    view: ExportStocksView,
    lotsByProduct: Record<number, ExportStockLot[]> = {},
): Promise<void> => {
    const workbook = new Workbook();
    const sheet = workbook.addWorksheet('Stocks');
    const columns = buildColumns(labels, view);
    sheet.columns = columns;

    const headerRow = sheet.getRow(1);
    headerRow.font = { bold: true };

    for (const stock of stocks) {
        const base = productCells(stock, view);
        if (!view.showLots) {
            sheet.addRow({
                ...base,
                countEnd: Number(stock.stockCount) || 0,
            });
            continue;
        }

        const lots = lotsByProduct[Number(stock.productId || 0)] || [];
        if (!lots.length) {
            sheet.addRow({
                ...base,
                price: null,
                priceLocal: null,
                rate: null,
                countEnd: 0,
            });
            continue;
        }

        for (const lot of lots) {
            sheet.addRow({
                ...base,
                price: lotMoney(lot.operPrice),
                priceLocal: view.showBothCurrencies ? lotMoney(lot.operPriceRate) : null,
                rate: view.showBothCurrencies ? lotRate(lot) : null,
                countEnd: Number(lot.stock) || 0,
            });
        }
    }

    const stamp = DT.now().toFormat('yyyyMMdd-HHmm');
    const fileName = `warehouse-stocks-${warehouseId}-${stamp}.xlsx`;
    const buffer = await workbook.xlsx.writeBuffer();
    downloadBlob(
        new Blob([buffer], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        }),
        fileName,
    );
};
